/**
 * Retell voice control — the "Voice & behaviour" section of each Retell
 * agent's card in Settings → AI Agent → Voice agents.
 *
 * Change that agent's voice, speed, stability and volume. Everything is read
 * live from Retell and written straight back, so the change applies to the
 * agent's next call.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { voiceAgentsService } from '../../lib/services';
import './VoiceAgents.css';

const errText = (e, fallback) => e?.response?.data?.message || e?.message || fallback;

const SLIDERS = [
  { key: 'voiceSpeed', name: 'Speed', min: 0.5, max: 2, low: 'Slower', high: 'Faster' },
  { key: 'voiceTemperature', name: 'Stability', min: 0, max: 2, low: 'Steady', high: 'Expressive' },
  { key: 'volume', name: 'Volume', min: 0, max: 2, low: 'Quieter', high: 'Louder' },
];

const voiceMeta = (v) => [v.gender, v.accent, v.provider].filter(Boolean).join(' · ');

export default function RetellVoiceControl({ agentId }) {
  const [voices, setVoices] = useState([]);
  const [form, setForm] = useState(null);
  const [orig, setOrig] = useState(null);
  const [busy, setBusy] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);
  const [filter, setFilter] = useState('');
  const audioRef = useRef(null);

  useEffect(() => {
    if (!agentId) return undefined;
    let alive = true;
    Promise.all([voiceAgentsService.getVoice(agentId), voiceAgentsService.listVoices(agentId)])
      .then(([v, list]) => {
        if (!alive) return;
        const f = {
          voiceId: v?.voiceId || '',
          voiceSpeed: v?.voiceSpeed ?? 1,
          voiceTemperature: v?.voiceTemperature ?? 1,
          volume: v?.volume ?? 1,
        };
        setForm(f); setOrig(f); setVoices(list || []);
      })
      .catch((e) => { if (alive) setMsg({ ok: false, text: errText(e, 'Could not read the voice from Retell') }); })
      .finally(() => { if (alive) setBusy(false); });
    return () => {
      alive = false;
      audioRef.current?.pause();
    };
  }, [agentId]);

  const set = (k, v) => { setForm((f) => ({ ...f, [k]: v })); setMsg(null); };
  const dirty = form && orig && Object.keys(form).some((k) => form[k] !== orig[k]);

  const shown = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return voices;
    return voices.filter((v) => [v.name, v.voiceId, v.provider, v.gender, v.accent]
      .some((x) => x && String(x).toLowerCase().includes(q)));
  }, [voices, filter]);

  const current = voices.find((v) => v.voiceId === form?.voiceId);

  const preview = (url) => {
    if (!url) return;
    audioRef.current?.pause();
    audioRef.current = new Audio(url);
    audioRef.current.play().catch(() => {});
  };

  const save = async () => {
    setSaving(true); setMsg(null);
    try {
      const changed = {};
      Object.keys(form).forEach((k) => { if (form[k] !== orig[k]) changed[k] = k === 'voiceId' ? form[k] : Number(form[k]); });
      const res = await voiceAgentsService.updateVoice(agentId, changed);
      const f = {
        voiceId: res?.voiceId || form.voiceId,
        voiceSpeed: res?.voiceSpeed ?? form.voiceSpeed,
        voiceTemperature: res?.voiceTemperature ?? form.voiceTemperature,
        volume: res?.volume ?? form.volume,
      };
      setForm(f); setOrig(f);
      setMsg({ ok: true, text: 'Applied — the next call uses it.' });
    } catch (e) {
      setMsg({ ok: false, text: errText(e, 'Could not update the voice') });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div className="va-panel-head">
        <div>
          <div className="va-panel-title">
            Voice{current ? ` — ${current.name || current.voiceId}` : ''}
          </div>
          <div className="va-panel-sub">How this agent sounds on calls. Written straight to Retell.</div>
        </div>
        {form && (
          <div className="va-actions">
            {msg && <span className={`va-msg ${msg.ok ? 'ok' : 'err'}`}>{msg.text}</span>}
            {dirty && !msg && <span className="pill warning filled">Unsaved</span>}
            {dirty && (
              <button type="button" className="btn small" disabled={saving} onClick={() => { setForm(orig); setMsg(null); }}>
                Discard
              </button>
            )}
            <button type="button" className="btn small primary" onClick={save} disabled={!dirty || saving}>
              {saving ? 'Applying…' : 'Apply voice'}
            </button>
          </div>
        )}
      </div>

      {busy && <div className="text-sm text-muted">Reading the voice from Retell…</div>}
      {!form && !busy && msg && <div className={`va-msg ${msg.ok ? 'ok' : 'err'}`}>{msg.text}</div>}

      {form && (
        <div className="rvc-grid">
          <div className="rvc-box">
            <div className="rvc-box-label">Voice · {voices.length} available</div>
            <input
              className="rvc-search"
              placeholder="Search by name, accent, gender…"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              aria-label="Search voices"
            />
            <div className="rvc-list" role="listbox" aria-label="Voices">
              {!current && form.voiceId && (
                <button type="button" role="option" aria-selected="true" className="rvc-voice selected">
                  <span className="rvc-voice-dot" />
                  <span className="rvc-voice-text">
                    <span className="rvc-voice-name">{form.voiceId}</span>
                    <span className="rvc-voice-meta">current voice</span>
                  </span>
                </button>
              )}
              {shown.map((v) => {
                const selected = v.voiceId === form.voiceId;
                return (
                  <div key={v.voiceId} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <button
                      type="button"
                      role="option"
                      aria-selected={selected}
                      className={`rvc-voice ${selected ? 'selected' : ''}`}
                      onClick={() => set('voiceId', v.voiceId)}
                    >
                      <span className="rvc-voice-dot" />
                      <span className="rvc-voice-text">
                        <span className="rvc-voice-name">{v.name || v.voiceId}</span>
                        {voiceMeta(v) && <span className="rvc-voice-meta">{voiceMeta(v)}</span>}
                      </span>
                    </button>
                    {v.previewUrl && (
                      <button
                        type="button"
                        className="rvc-play"
                        onClick={() => preview(v.previewUrl)}
                        title={`Preview ${v.name || v.voiceId}`}
                        aria-label={`Preview ${v.name || v.voiceId}`}
                      >
                        ▶
                      </button>
                    )}
                  </div>
                );
              })}
              {shown.length === 0 && <div className="rvc-empty">No voices match “{filter}”.</div>}
            </div>
          </div>

          <div className="rvc-box">
            <div className="rvc-box-label">Delivery</div>
            {SLIDERS.map((s) => (
              <label key={s.key} className="rvc-slider" style={{ display: 'block' }}>
                <div className="rvc-slider-head">
                  <span className="rvc-slider-name">{s.name}</span>
                  <span className="rvc-slider-value">{Number(form[s.key]).toFixed(2)}</span>
                </div>
                <input
                  type="range"
                  min={s.min}
                  max={s.max}
                  step={0.05}
                  value={form[s.key]}
                  onChange={(e) => set(s.key, Number(e.target.value))}
                />
                <div className="rvc-slider-hint"><span>{s.low}</span><span>{s.high}</span></div>
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
